import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { 
  FileText, 
  Clock, 
  MessageSquare, 
  Mail, 
  Phone, 
  Upload,
  Download,
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CaseRepository = ({ user, onLogout }) => {
  const { case_id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [activeTab, setActiveTab] = useState('timeline');
  const [loading, setLoading] = useState(true);
  const [commFilter, setCommFilter] = useState('all');

  useEffect(() => {
    fetchRepository();
  }, [case_id]);

  const fetchRepository = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/repository/${case_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCaseData(res.data.case);
      setActivities(res.data.activities);
      setDocuments(res.data.documents);
      setCommunications(res.data.communications);
    } catch (err) {
      console.error('Failed to fetch repository:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout user={user} onLogout={onLogout}>
        <div>Loading...</div>
      </MainLayout>
    );
  }

  if (!caseData) {
    return (
      <MainLayout user={user} onLogout={onLogout}>
        <div className="error-message">Case not found</div>
      </MainLayout>
    );
  }

  const filteredComms = commFilter === 'all' 
    ? communications 
    : communications.filter(c => c.communication_type === commFilter);

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={() => navigate('/cases')}
          className="btn-secondary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <ArrowLeft size={18} />
          Back to Cases
        </button>
      </div>

      {/* Case Header */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 className="page-title" style={{ margin: '0 0 0.5rem 0' }}>
              {caseData.case_number || 'Case Details'}
            </h1>
            <p style={{ color: '#718096', margin: '0 0 1rem 0' }}>
              Agreement No: <strong>{caseData.agreement_no}</strong> | 
              Batch: <strong>{caseData.batch_name}</strong>
            </p>
            <span className={`badge badge-${caseData.stage === 'new' ? 'new' : caseData.stage.includes('Stage') ? 'ongoing' : 'closed'}`}>
              {caseData.stage_name || caseData.stage}
            </span>
          </div>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1.5rem',
          marginTop: '1.5rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid #e2e8f0'
        }}>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>Claimant</div>
            <div style={{ fontWeight: 600 }}>{caseData.claimant_id?.name || '-'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>AR</div>
            <div style={{ fontWeight: 600 }}>{caseData.ar_id?.name || '-'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>Arbitrator</div>
            <div style={{ fontWeight: 600 }}>{caseData.arbitrator_id?.name || '-'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.25rem' }}>Created</div>
            <div style={{ fontWeight: 600 }}>
              {new Date(caseData.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[
            { id: 'timeline', label: 'Timeline', icon: Clock },
            { id: 'documents', label: 'Documents', icon: FileText },
            { id: 'communications', label: 'Communications', icon: MessageSquare }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '3px solid #667eea' : '3px solid transparent',
                  color: activeTab === tab.id ? '#667eea' : '#718096',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Icon size={18} />
                {tab.label}
                {tab.id === 'documents' && documents.length > 0 && (
                  <span className="badge badge-new" style={{ marginLeft: '0.5rem' }}>
                    {documents.length}
                  </span>
                )}
                {tab.id === 'communications' && communications.length > 0 && (
                  <span className="badge badge-new" style={{ marginLeft: '0.5rem' }}>
                    {communications.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Activity Timeline</h2>
          </div>
          {activities.length === 0 ? (
            <div className="empty-state">
              <Clock size={48} />
              <div className="empty-state-text">No activities yet</div>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              {/* Timeline line */}
              <div style={{
                position: 'absolute',
                left: '20px',
                top: '0',
                bottom: '0',
                width: '2px',
                background: '#e2e8f0'
              }} />
              
              {activities.map((activity, index) => (
                <div 
                  key={activity._id || index}
                  data-testid="activity-item"
                  style={{ 
                    position: 'relative',
                    paddingLeft: '3rem',
                    paddingBottom: '1.5rem'
                  }}
                >
                  {/* Timeline dot */}
                  <div style={{
                    position: 'absolute',
                    left: '12px',
                    top: '4px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    background: activity.type === 'stage_updated' ? '#48bb78' : '#667eea',
                    border: '3px solid white',
                    boxShadow: '0 0 0 1px ' + (activity.type === 'stage_updated' ? '#48bb78' : '#667eea')
                  }} />
                  
                  <div style={{
                    background: '#f7fafc',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 600, color: '#1a202c' }}>
                        {activity.type === 'stage_updated' ? 'Stage Transitioned' : activity.description}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#a0aec0' }}>
                        {new Date(activity.timestamp).toLocaleString()}
                      </span>
                    </div>

                    {activity.type === 'stage_updated' && activity.metadata?.previous_stage && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '1rem', 
                        margin: '0.75rem 0',
                        padding: '0.5rem',
                        background: 'white',
                        borderRadius: '6px',
                        border: '1px dashed #cbd5e0'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Old Stage</div>
                          <div style={{ fontSize: '0.875rem', color: '#e53e3e', fontWeight: 500 }}>{activity.metadata.previous_stage}</div>
                        </div>
                        <div style={{ color: '#cbd5e0', fontSize: '1.25rem' }}>→</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase' }}>Current Stage</div>
                          <div style={{ fontSize: '0.875rem', color: '#38a169', fontWeight: 600 }}>{activity.metadata.new_stage}</div>
                        </div>
                      </div>
                    )}

                    {activity.type === 'case_status_changed' && activity.metadata?.action && (
                        <div style={{ 
                          margin: '0.5rem 0',
                          padding: '0.5rem',
                          background: '#fff5f5',
                          borderRadius: '6px',
                          border: '1px solid #feb2b2',
                          display: 'inline-block'
                        }}>
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#c53030' }}>
                            Action: {activity.metadata.action.toUpperCase()}
                          </span>
                          {activity.metadata.previous_stage && (
                            <div style={{ fontSize: '0.75rem', color: '#718096' }}>
                              From: {activity.metadata.previous_stage}
                            </div>
                          )}
                        </div>
                    )}

                    <div style={{ fontSize: '0.875rem', color: '#718096' }}>
                      {activity.type !== 'stage_updated' && activity.description && (
                        <div style={{ marginBottom: '0.25rem' }}>{activity.description}</div>
                      )}
                      {activity.metadata?.comment && (
                        <div style={{ fontStyle: 'italic', marginBottom: '0.5rem', color: '#4a5568' }}>
                          "{activity.metadata.comment}"
                        </div>
                      )}
                      by <strong>{activity.user_name}</strong> ({activity.user_role?.replace('_', ' ')})
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">Documents</h2>
            {user.role === 'admin' && (
              <button className="btn-icon" style={{ padding: '0.5rem 1rem' }}>
                <Upload size={18} />
                Upload Document
              </button>
            )}
          </div>
          {documents.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <div className="empty-state-text">No documents uploaded</div>
            </div>
          ) : (
            <table data-testid="documents-table" className="data-table">
              <thead>
                <tr>
                  <th>Document Name</th>
                  <th>Type</th>
                  <th>Uploaded By</th>
                  <th>Date</th>
                  <th>Size</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc._id}>
                    <td style={{ fontWeight: 600 }}>{doc.document_name}</td>
                    <td>
                      <span className="badge badge-new">
                        {doc.document_type}
                      </span>
                    </td>
                    <td>{doc.uploaded_by_name}</td>
                    <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                    <td>{doc.file_size ? `${(doc.file_size / 1024).toFixed(2)} KB` : '-'}</td>
                    <td>
                      <button
                        className="btn-secondary"
                        style={{ padding: '0.5rem' }}
                      >
                        <Download size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Communications Tab */}
      {activeTab === 'communications' && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">Communication History</h2>
            <select
              data-testid="comm-filter"
              className="filter-select"
              value={commFilter}
              onChange={(e) => setCommFilter(e.target.value)}
            >
              <option value="all">All Communications</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          {filteredComms.length === 0 ? (
            <div className="empty-state">
              <MessageSquare size={48} />
              <div className="empty-state-text">No communications yet</div>
            </div>
          ) : (
            <div>
              {filteredComms.map((comm) => {
                const Icon = comm.communication_type === 'email' ? Mail : Phone;
                return (
                  <div
                    key={comm._id}
                    data-testid="communication-item"
                    style={{
                      padding: '1rem',
                      borderBottom: '1px solid #e2e8f0',
                      display: 'flex',
                      gap: '1rem'
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      background: comm.communication_type === 'email' ? '#bee3f8' : '#feebc8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Icon size={20} color={comm.communication_type === 'email' ? '#2c5282' : '#7c2d12'} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <div>
                          <span className="badge badge-new" style={{ marginRight: '0.5rem' }}>
                            {comm.communication_type.toUpperCase()}
                          </span>
                          <span style={{ fontWeight: 600 }}>
                            To: {comm.recipient}
                          </span>
                        </div>
                        <span style={{ fontSize: '0.875rem', color: '#718096' }}>
                          {new Date(comm.sent_at).toLocaleString()}
                        </span>
                      </div>
                      {comm.subject && (
                        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                          {comm.subject}
                        </div>
                      )}
                      <div style={{ fontSize: '0.875rem', color: '#4a5568' }}>
                        {comm.message}
                      </div>
                      <div style={{ 
                        marginTop: '0.5rem',
                        fontSize: '0.75rem',
                        color: comm.status === 'sent' ? '#38a169' : '#e53e3e'
                      }}>
                        Status: {comm.status}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </MainLayout>
  );
};

export default CaseRepository;
